import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { todayISO, inr, GST_OPTIONS } from "@/lib/utils2";
import { toast } from "sonner";

const empty = {
  entry_date: todayISO(),
  vehicle_number: "",
  party_id: "",
  transporter_id: "",
  consignor_name: "",
  consignee_name: "",
  // material
  material_id: "",
  material_weight: 0,
  material_purchase_rate: 0,
  material_bill_rate: 0,
  material_gst_percent: 5,
  po_number: "",
  po_date: "",
  unloading_date: "",
  // transport
  transport_weight: 0,
  transport_purchase_rate: 0,
  transport_bill_rate: 0,
  transport_gst_percent: 0,
  // combined billing (inclusive ₹/unit covering material + freight to party)
  billing_mode: "split",
  combined_bill_rate: 0,
  combined_gst_percent: 5,
  // purchase mode — independent of sale/billing mode.
  // e.g. PURCHASED from supplier combined @2000/MT, SOLD split @1000+1150/MT.
  purchase_mode: "split",
  combined_purchase_rate: 0,
  // inbound (purchase-side) GST charged by supplier/transporter to us
  purchase_gst_percent: 0,
  purchase_gst_invoice_number: "",
  notes: "",
};

export default function TruckEntry() {
  const { id } = useParams();
  const editing = !!id;
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [parties, setParties] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [pos, setPos] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, t, m, po] = await Promise.all([
        api.get("/parties"), api.get("/transporters"), api.get("/materials"), api.get("/pos"),
      ]);
      setParties(p.data); setTransporters(t.data); setMaterials(m.data); setPos(po.data);
      if (editing) {
        const { data } = await api.get(`/trucks/${id}`);
        setForm({ ...empty, ...data });
      }
    })();
  }, [id, editing]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const num = (k, v) => set(k, v === "" ? 0 : parseFloat(v));

  // When user picks a party, auto-fill billing mode + combined defaults from that party
  // (only for NEW entries, so we don't clobber a saved truck).
  const onPickParty = (pid) => {
    const p = parties.find((x) => x.id === pid);
    if (!editing && p) {
      setForm((s) => ({
        ...s,
        party_id: pid,
        billing_mode: p.default_billing_mode || "split",
        combined_bill_rate: p.default_combined_rate || s.combined_bill_rate,
        combined_gst_percent: p.default_gst_percent ?? s.combined_gst_percent,
      }));
    } else {
      set("party_id", pid);
    }
  };

  // Auto-mirror weight when single weight workflow (most common): if user enters material_weight
  // and transport_weight is 0, default transport_weight to same value on blur (only if same trip).
  const mirrorWeight = () => {
    if (form.transport_weight === 0 && form.material_weight > 0 && form.transport_bill_rate > 0) {
      set("transport_weight", form.material_weight);
    }
  };

  const mw = Number(form.material_weight) || 0;
  const tw = Number(form.transport_weight) || 0;
  const cbw = mw || tw;
  const isCombinedPurchase = form.purchase_mode === "combined";
  const combinedPurchase = cbw * (Number(form.combined_purchase_rate) || 0);
  const matPurchase = isCombinedPurchase ? combinedPurchase : mw * (Number(form.material_purchase_rate) || 0);
  const trnPurchase = isCombinedPurchase ? 0 : tw * (Number(form.transport_purchase_rate) || 0);
  const matBill = mw * (Number(form.material_bill_rate) || 0);
  const trnBill = tw * (Number(form.transport_bill_rate) || 0);
  const combinedBill = cbw * (Number(form.combined_bill_rate) || 0);
  const purchaseBase = matPurchase + trnPurchase;
  const purchaseGst = purchaseBase * (Number(form.purchase_gst_percent) || 0) / 100;
  const totalOwedToTransporter = purchaseBase + purchaseGst;
  const isCombined = form.billing_mode === "combined";
  const saleTotal = isCombined ? combinedBill : (matBill + trnBill);
  const profit = saleTotal - purchaseBase;

  const save = async (e) => {
    e.preventDefault();
    if (!form.party_id) return toast.error("Select a party");
    if (!form.vehicle_number) return toast.error("Vehicle number required");
    if (isCombined) {
      if (!(form.combined_bill_rate > 0)) return toast.error("Combined sale rate required (₹/unit)");
      if (!(cbw > 0)) return toast.error("Enter material or transport weight for combined billing");
    } else {
      const hasMaterial = form.material_id && form.material_bill_rate > 0;
      const hasTransport = form.transport_bill_rate > 0;
      if (!hasMaterial && !hasTransport) {
        return toast.error("Enter material details and/or transport details");
      }
    }
    if (isCombinedPurchase) {
      if (!(form.combined_purchase_rate > 0)) return toast.error("Combined purchase rate required (₹/unit)");
      if (!(cbw > 0)) return toast.error("Enter material or transport weight for combined purchase");
    }
    const payload = { ...form };
    if (!payload.transporter_id) payload.transporter_id = null;
    if (!payload.material_id) payload.material_id = null;
    try {
      if (editing) await api.put(`/trucks/${id}`, payload);
      else await api.post("/trucks", payload);
      toast.success(editing ? "Updated" : "Truck released entry saved");
      nav("/trucks");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-5xl mx-auto" data-testid="truck-entry-page">
      <PageHeader
        title={editing ? "Edit Truck Entry" : "New Truck Release"}
        subtitle="One entry per truck release — captures Material side, Transport side & GST in a single step."
      />

      <form onSubmit={save} className="space-y-6">
        {/* Common */}
        <Card className="rounded-sm border-brand-line shadow-none p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mb-4">1. Common Details</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Entry Date *</Label>
              <Input type="date" data-testid="entry-date" value={form.entry_date}
                onChange={(e) => set("entry_date", e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white" required />
            </div>
            <div>
              <Label>Vehicle Number *</Label>
              <Input data-testid="vehicle-number" value={form.vehicle_number}
                onChange={(e) => set("vehicle_number", e.target.value.toUpperCase())}
                className="mt-1.5 h-10 rounded-sm bg-white uppercase" placeholder="WB37H-1907" required />
            </div>
            <div>
              <Label>Party (Bill To) *</Label>
              <Select value={form.party_id} onValueChange={onPickParty}>
                <SelectTrigger data-testid="party-select" className="mt-1.5 h-10 rounded-sm bg-white">
                  <SelectValue placeholder="Select party" />
                </SelectTrigger>
                <SelectContent>
                  {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.default_billing_mode === "combined" ? "  ·  Combined" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Transporter (Consignor)</Label>
              <Select value={form.transporter_id || "none"} onValueChange={(v) => set("transporter_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="transporter-select" className="mt-1.5 h-10 rounded-sm bg-white">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {transporters.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Consignor Name (free text)</Label>
              <Input value={form.consignor_name} onChange={(e) => set("consignor_name", e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white" placeholder="If not in list" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => set("notes", e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white" />
            </div>
          </div>
        </Card>

        {/* Billing mode picker */}
        <Card className="rounded-sm border-brand-line shadow-none p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">2. Purchase & Sale Mode</div>
            <div className="text-[10px] text-brand-muted">Purchase and Sale modes are independent — pick any combination</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Purchase Mode *</Label>
              <Select value={form.purchase_mode} onValueChange={(v) => set("purchase_mode", v)}>
                <SelectTrigger data-testid="purchase-mode-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="split">Split — Material + Freight purchased separately</SelectItem>
                  <SelectItem value="combined">Combined — Single inclusive rate from supplier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isCombinedPurchase && (
              <div className="md:col-span-2">
                <Label>Combined Purchase Rate (₹/unit, supplier all-in)</Label>
                <Input type="number" step="0.01" data-testid="combined-purchase-rate"
                  value={form.combined_purchase_rate} onChange={(e) => num("combined_purchase_rate", e.target.value)}
                  className="mt-1.5 h-10 rounded-sm bg-white tabular-nums" placeholder="e.g. 2000" />
                <div className="text-[10px] text-brand-muted mt-1">Used instead of the separate purchase-rate fields below.</div>
              </div>
            )}
            <div>
              <Label>Sale / Billing Mode *</Label>
              <Select value={form.billing_mode} onValueChange={(v) => set("billing_mode", v)}>
                <SelectTrigger data-testid="billing-mode-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="split">Split — Material + Transport billed separately to party</SelectItem>
                  <SelectItem value="combined">Combined — One inclusive rate to party</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isCombined && (
              <>
                <div>
                  <Label>Combined Bill Rate (₹/unit, inclusive)</Label>
                  <Input type="number" step="0.01" data-testid="combined-bill-rate"
                    value={form.combined_bill_rate} onChange={(e) => num("combined_bill_rate", e.target.value)}
                    className="mt-1.5 h-10 rounded-sm bg-white tabular-nums" />
                </div>
                <div>
                  <Label>Combined GST %</Label>
                  <Select value={String(form.combined_gst_percent)} onValueChange={(v) => set("combined_gst_percent", Number(v))}>
                    <SelectTrigger data-testid="combined-gst-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GST_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          <div className="mt-5 pt-5 border-t border-brand-line grid grid-cols-1 md:grid-cols-4 gap-4">
            <Stat label="Weight basis (MT)" value={cbw.toFixed(2)} />
            <Stat label="Purchase Cost (basic)" value={inr(purchaseBase)} />
            <Stat label={isCombined ? "Sale (Combined)" : "Sale (Mat + Trn)"} value={inr(saleTotal)} highlight />
            <Stat label="Profit (Sale − Purchase)" value={inr(profit)} highlight />
          </div>
          {(isCombined || isCombinedPurchase) && (
            <p className="text-[11px] text-brand-muted mt-3">
              {isCombinedPurchase && "Combined purchase: enter only the all-in supplier rate above — individual purchase-rate fields below are ignored. "}
              {isCombined && "Combined sale: party-facing bill rate fields below are not used."}
            </p>
          )}
        </Card>

        {/* Material */}
        <Card className="rounded-sm border-brand-line shadow-none p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">3. Material Side</div>
            <div className="text-[10px] text-brand-muted">
              {isCombinedPurchase && isCombined ? "Combined purchase + combined sale: purchase/bill rate fields below are ignored" :
               isCombinedPurchase ? "Combined purchase: purchase rate field below is ignored" :
               isCombined ? "Combined sale: bill rate field below is ignored" :
               "Leave empty to skip material billing for this truck"}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label>Material</Label>
              <Select value={form.material_id || "none"} onValueChange={(v) => set("material_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="material-select" className="mt-1.5 h-10 rounded-sm bg-white">
                  <SelectValue placeholder="Select material (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} {m.hsn ? `· HSN ${m.hsn}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Material GST %</Label>
              <Select value={String(form.material_gst_percent)} onValueChange={(v) => set("material_gst_percent", Number(v))}>
                <SelectTrigger data-testid="material-gst-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GST_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Weight (MT)</Label>
              <Input type="number" step="0.001" data-testid="material-weight"
                value={form.material_weight}
                onChange={(e) => num("material_weight", e.target.value)}
                onBlur={mirrorWeight}
                className="mt-1.5 h-10 rounded-sm bg-white tabular-nums" />
            </div>
            <div>
              <Label>Purchase Rate (₹/MT)</Label>
              <Input type="number" step="0.01" value={form.material_purchase_rate}
                onChange={(e) => num("material_purchase_rate", e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white tabular-nums" />
            </div>
            <div>
              <Label>Bill Rate (₹/MT)</Label>
              <Input type="number" step="0.01" data-testid="material-bill-rate"
                value={form.material_bill_rate}
                onChange={(e) => num("material_bill_rate", e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white tabular-nums" />
            </div>
            <div>
              <Label>PO Number (from list)</Label>
              <Select value={form.po_number || "none"} onValueChange={(v) => {
                if (v === "none") { set("po_number", ""); set("po_date", ""); return; }
                const po = pos.find((x) => x.po_number === v && (!form.party_id || x.party_id === form.party_id));
                set("po_number", v);
                if (po?.po_date) set("po_date", po.po_date);
              }}>
                <SelectTrigger data-testid="po-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue placeholder="Select PO" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {pos
                    .filter((p) => !form.party_id || p.party_id === form.party_id)
                    .map((p) => <SelectItem key={p.id} value={p.po_number}>{p.po_number} · {p.po_date}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-brand-muted mt-1">Manage POs in Masters → Purchase Orders.</div>
            </div>
            <div>
              <Label>PO Date</Label>
              <Input type="date" value={form.po_date} onChange={(e) => set("po_date", e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white" />
            </div>
            <div>
              <Label>Unloading Date</Label>
              <Input type="date" value={form.unloading_date} onChange={(e) => set("unloading_date", e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-5 pt-5 border-t border-brand-line">
            <Stat label="Mat. Purchase Amt" value={inr(matPurchase)} />
            <Stat label="Mat. Bill Amt" value={inr(matBill)} />
            <Stat label="Mat. Profit" value={inr(matBill - matPurchase)} highlight />
          </div>
        </Card>

        {/* Transport */}
        <Card className="rounded-sm border-brand-line shadow-none p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">4. Transport Side</div>
            <div className="text-[10px] text-brand-muted">
              {isCombinedPurchase && isCombined ? "Combined purchase + combined sale: purchase/bill rate fields below are ignored" :
               isCombinedPurchase ? "Combined purchase: purchase rate field below is ignored" :
               isCombined ? "Combined sale: bill rate field below is ignored" :
               "Leave bill rate as 0 to skip transport billing"}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Transport Weight (MT)</Label>
              <Input type="number" step="0.001" data-testid="transport-weight"
                value={form.transport_weight} onChange={(e) => num("transport_weight", e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white tabular-nums" />
            </div>
            <div>
              <Label>Purchase Rate (₹/MT)</Label>
              <Input type="number" step="0.01" value={form.transport_purchase_rate}
                onChange={(e) => num("transport_purchase_rate", e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white tabular-nums" />
            </div>
            <div>
              <Label>Bill Rate (₹/MT)</Label>
              <Input type="number" step="0.01" data-testid="transport-bill-rate"
                value={form.transport_bill_rate} onChange={(e) => num("transport_bill_rate", e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white tabular-nums" />
            </div>
            <div>
              <Label>Transport GST %</Label>
              <Select value={String(form.transport_gst_percent)} onValueChange={(v) => set("transport_gst_percent", Number(v))}>
                <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GST_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-5 pt-5 border-t border-brand-line">
            <Stat label="Trn. Purchase Amt" value={inr(trnPurchase)} />
            <Stat label="Trn. Bill Amt" value={inr(trnBill)} />
            <Stat label="Trn. Profit" value={inr(trnBill - trnPurchase)} highlight />
          </div>
        </Card>

        {/* Inbound (purchase-side) GST — when the supplier/transporter charges us GST */}
        <Card className="rounded-sm border-brand-line shadow-none p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">5. Inbound GST (paid to transporter / supplier)</div>
            <div className="text-[10px] text-brand-muted">Optional — set if transporter/supplier issues us a tax invoice</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Purchase GST % (charged to us)</Label>
              <Select value={String(form.purchase_gst_percent)}
                onValueChange={(v) => set("purchase_gst_percent", Number(v))}>
                <SelectTrigger data-testid="purchase-gst-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% (unregistered / RCM)</SelectItem>
                  <SelectItem value="5">5%</SelectItem>
                  <SelectItem value="12">12%</SelectItem>
                  <SelectItem value="18">18%</SelectItem>
                  <SelectItem value="28">28%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Their Invoice / Bilty No.</Label>
              <Input data-testid="purchase-gst-invoice" value={form.purchase_gst_invoice_number}
                onChange={(e) => set("purchase_gst_invoice_number", e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white" placeholder="e.g. SUP/INV/2026-001" />
            </div>
            <div></div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-5 pt-5 border-t border-brand-line">
            <Stat label="Purchase Base (Mat + Trn)" value={inr(purchaseBase)} />
            <Stat label="GST on Purchase" value={inr(purchaseGst)} />
            <Stat label="Total Payable to Transporter" value={inr(totalOwedToTransporter)} highlight />
          </div>
        </Card>

        <Card className="rounded-sm border-brand-forest bg-brand-forest text-white shadow-none p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-sand mb-3">Combined Profit</div>
          <div className="font-serif text-4xl tracking-tight tabular-nums">{inr(profit)}</div>
        </Card>

        <div className="flex justify-end gap-3 sticky bottom-0 bg-background/95 backdrop-blur py-4 -mx-2 px-2 border-t border-brand-line">
          <Button type="button" variant="outline" onClick={() => nav(-1)} className="rounded-sm">Cancel</Button>
          <Button type="submit" data-testid="save-truck-btn" className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
            {editing ? "Update Entry" : "Save Truck Entry"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">{label}</div>
      <div className={`mt-1 font-serif text-xl tabular-nums ${highlight ? "text-brand-forest" : "text-brand-ink"}`}>
        {value}
      </div>
    </div>
  );
}
